/**
 * Keeps the blurred backdrop on the immersive product header showing whichever
 * gallery image the shopper is actually looking at.
 *
 * The theme's gallery does not announce a slide change, so this watches for the
 * class Swiper puts on the active slide rather than reaching into the slider
 * instance. That keeps it working whether the gallery is a swiper, a stacked
 * list, or something the theme changes to later: the only contract is "the
 * active slide is marked, and it contains an image".
 */
if (!customElements.get('product-immersive')) {
  customElements.define(
    'product-immersive',
    class ProductImmersive extends HTMLElement {
      connectedCallback() {
        /* connectedCallback runs on every insertion, and a second observer on
           the same gallery would mean two swaps racing for one property. */
        if (this.wired) return
        this.wired = true

        this.section = this.closest('.section-main-product--immersive')
        this.gallery = this.section && this.section.querySelector('.product__media-list')
        if (!this.gallery) return

        this.apply = this.apply.bind(this)
        this.observer = new MutationObserver(() => {
          cancelAnimationFrame(this.frame)
          this.frame = requestAnimationFrame(this.apply)
        })
        this.observer.observe(this.gallery, {
          subtree: true,
          attributes: true,
          attributeFilter: ['class'],
        })

        /* The first image often is not decoded when this runs, and currentSrc is
           empty until it is, so try again once the gallery has loaded. */
        this.apply()
        window.addEventListener('load', this.apply, { once: true })
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect()
        cancelAnimationFrame(this.frame)
      }

      /** The image in the active slide, falling back to the first one. */
      get activeSource() {
        const slide =
          this.gallery.querySelector('.swiper-slide-active') ||
          this.gallery.querySelector('.is-active') ||
          this.gallery.firstElementChild
        const img = slide && slide.querySelector('img')
        if (!img) return null
        const src = img.currentSrc || img.getAttribute('src')
        if (!src || src.indexOf('data:') === 0) return null

        /* Normalise the width. currentSrc gives whatever the browser picked for
           the slot, which for a lazily loaded slide can be 690px and for the
           first one 1620px, so the backdrop's sharpness would change with which
           image is showing. 1200 is more than a 14px blur can use. */
        return src.replace(/([?&]width=)\d+/, '$11200')
      }

      apply() {
        const src = this.activeSource
        if (!src || src === this.current) return
        this.current = src

        /* A hard cut on a full bleed backdrop is jarring, so it dips out and
           back rather than snapping. The transition lives in the stylesheet;
           this only moves the flag. */
        this.section.classList.add('is-swapping')
        clearTimeout(this.settle)
        this.settle = setTimeout(() => {
          this.section.style.setProperty('--product-hero', `url(${src})`)
          this.section.classList.remove('is-swapping')
        }, 180)
      }
    }
  )
}
